import { UseFormReturn } from "react-hook-form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import type { OrganizationProfileFormData } from "./organizationProfileForm.schema";

export type Props = {
  /** Form instance from useForm */
  form: UseFormReturn<OrganizationProfileFormData>;
  /** Handler for form submission */
  onSubmit: () => void;
  /** Handler for cancel/reset */
  onCancel: () => void;
  /** Whether the form has been modified */
  isDirty: boolean;
  /** Whether the form is valid */
  isValid: boolean;
  /** Whether the form is submitting */
  isSubmitting: boolean;
};

/**
 * Organization profile form component.
 * This is a presentational component - business logic should be in the container.
 */
export function OrganizationProfileForm({
  form,
  onSubmit,
  onCancel,
  isDirty,
  isValid,
  isSubmitting,
}: Props) {
  return (
    <div
      className="flex flex-col gap-4 w-full"
      data-testid="organization-profile-form"
    >
      <Form form={form} errorContainerClassName="p-0 w-full max-w-full mt-8">
        <FormField
          name="name"
          label="Display Name"
          helpText="This is how your participants will see your organization name displayed."
          component="Input"
          placeholder="Enter your organization display name"
          layout="horizontal"
          required
        />

        <SectionHeading
          title="Social Accounts"
          description="Add your organization's social media handles to help participants connect with you."
          className="mt-8"
        >
          <div className="flex flex-col gap-4">
            <FormField
              name="instagram_url"
              label="Instagram"
              component="Input"
              prefix="instagram.com/"
              placeholder="username"
              layout="horizontal"
            />
            <FormField
              name="facebook_url"
              label="Facebook"
              component="Input"
              prefix="facebook.com/"
              placeholder="page-name"
              layout="horizontal"
            />
            <FormField
              name="tiktok_url"
              label="TikTok"
              component="Input"
              prefix="tiktok.com/"
              placeholder="@username"
              layout="horizontal"
            />
            <FormField
              name="bluesky_url"
              label="Bluesky"
              component="Input"
              prefix="bsky.app/profile/"
              placeholder="handle.bsky.social"
              layout="horizontal"
            />
            <FormField
              name="linkedin_url"
              label="LinkedIn"
              component="Input"
              prefix="linkedin.com/company/"
              placeholder="company-name"
              layout="horizontal"
            />
          </div>
        </SectionHeading>
      </Form>
      <ActionButtonsGroup
        primaryButtonText="Save"
        onPrimaryClick={onSubmit}
        secondaryButtonText={isDirty ? "Cancel" : undefined}
        onSecondaryClick={isDirty ? onCancel : undefined}
        className="size-max w-fit self-end"
        primaryButtonProps={{
          disabled: !isDirty || isSubmitting,
          visuallyDisabled: isDirty && !isSubmitting && !isValid,
        }}
      />
    </div>
  );
}
